import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { Icon } from '../../lib/icons.js';
import { haptic } from '../../lib/native.js';

/**
 * Press and hold to confirm (unlock a door, open the street door). A ring
 * fills over `ms`; releasing early cancels. Keyboard: hold Space/Enter.
 */
export function HoldButton({
  label,
  holdingLabel = 'Keep holding…',
  icon,
  ms = 600,
  onConfirm,
  disabled,
  tone = 'accent',
  testId,
}: {
  label: string;
  holdingLabel?: string;
  icon: string;
  ms?: number;
  onConfirm: () => void;
  disabled?: boolean;
  tone?: 'accent' | 'alert';
  testId?: string;
}): ReactNode {
  const [holding, setHolding] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const start = (): void => {
    if (disabled || timer.current) return;
    setHolding(true);
    haptic('light');
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      setHolding(false);
      haptic('success');
      onConfirm();
    }, ms);
  };
  const stop = (): void => {
    if (!timer.current) return;
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setHolding(false);
  };

  return (
    <button
      type="button"
      className="hold-btn"
      data-tone={tone}
      data-holding={holding ? 'true' : undefined}
      data-no-hold
      disabled={disabled}
      style={{ ['--hold-ms' as string]: `${ms}ms` }}
      data-testid={testId}
      onPointerDown={(e: PointerEvent<HTMLButtonElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        start();
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
          e.preventDefault();
          start();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === ' ' || e.key === 'Enter') stop();
      }}
    >
      <span className="hold-fill" aria-hidden="true" />
      <Icon name={icon} size={17} />
      <span>{holding ? holdingLabel : label}</span>
    </button>
  );
}
